import type { ProductAreaLevel } from "@ttc-spectree/toolbox-contract";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

export interface AreaListEntry {
  area: string;
  level: ProductAreaLevel;
  what: string;
}

const LEVEL_LABELS: Record<ProductAreaLevel, string> = {
  main: "Main change",
  small: "Small change",
  check: "Check only",
};

const LEVEL_DOTS: Record<ProductAreaLevel, string> = {
  main: "bg-primary",
  small: "bg-amber-500",
  check: "bg-zinc-600",
};

/**
 * "What gets touched?": the product areas a change touches, in the plain
 * names of the project's product-area list, each with a level dot (main red,
 * small amber, check grey). Claude Design "Final PM" frame 5b; restyled to
 * the v2 desktop design's flat rows (`.area`) in epic PI-SPEC-SW-E280, task
 * PI-SPEC-SW-1405-1. Every string is model output: rendered as text only.
 */
export function AreaList({ areas }: { areas: readonly AreaListEntry[] }) {
  return (
    <ul className="flex flex-col" data-testid="area-list">
      {areas.map((entry, index) => (
        <li
          key={`${entry.area}-${String(index)}`}
          className="flex items-start gap-2.5 py-1"
          data-testid="area-row"
          data-level={entry.level}
        >
          <span className={cn("mt-[5px] h-2 w-2 shrink-0 rounded-full", LEVEL_DOTS[entry.level])} aria-hidden="true" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-medium">{entry.area}</span>
            <span className="text-[13px] leading-relaxed text-muted-foreground">{entry.what}</span>
          </div>
          <span className={cn("shrink-0 text-xs", TOOLBOX_TEXT.faint)}>{LEVEL_LABELS[entry.level]}</span>
        </li>
      ))}
    </ul>
  );
}
